import { useEffect, useState } from 'react'

// A directly-embedded casino game (e.g. Tetris) binds its own keydown/keyup listeners on
// `document`, with no iframe boundary and no focusable element to scope them to (see
// casino/docs/adr/0003-game-implementation-tips.md). Host-level keyboard shortcuts (this app's
// GlobalShortcuts and Sidebar) share that same document, so a game signals "I own the keyboard
// right now" by dispatching a plain `game:focus` window event on mount and `game:blur` on
// unmount — the same window-event bridge pattern already used for `game:unload` etc. Pure
// DOM/window code, no framework deps, so it bundles into every widget with no shimming.

let active = false
const listeners = new Set<(active: boolean) => void>()

if (typeof window !== 'undefined') {
  window.addEventListener('game:focus', () => {
    active = true
    listeners.forEach(l => l(true))
  })
  window.addEventListener('game:blur', () => {
    active = false
    listeners.forEach(l => l(false))
  })
}

export function isGameActive(): boolean {
  return active
}

export function useGameActive(): boolean {
  const [value, setValue] = useState(isGameActive)
  useEffect(() => {
    listeners.add(setValue)
    return () => {
      listeners.delete(setValue)
    }
  }, [])
  return value
}
